<script lang="ts" setup>
import { onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useWeb3AuthStore } from "~/stores/storeWeb3auth";
import { useStoreUserV2 } from "~/stores/storeUserV2";
import { FormStatus } from "../formsV2/types";
import Modal from "@/components/modals/ModalContainer.vue";
import { auctionApi } from "~/api/auction";
import { useStoreApp } from "~/stores/storeApp";
import { Users, X } from "lucide-vue-next";

const storeApp = useStoreApp();

const { t } = useI18n();

const userStore = useStoreUserV2();

const emits = defineEmits<{
  (e: "on-close"): void;
}>();

const loading = ref(false);

onMounted(() => {});

const usernameFormErrors = ref<FormStatus | null>(null);
const username = ref<string>("");
const referralInfo = ref<{
  username: string;
  referralCode: string;
} | null>();

const handleUsernameOnChange = async (value: string) => {
  username.value = value;
  if (value.trim().includes(" ")) {
    usernameFormErrors.value = {
      type: "error",
      msg: t("pages.auth.form.errors.spaceNotAllowed"),
    };
  } else if (usernameFormErrors.value?.type === "error") {
    usernameFormErrors.value = null;
  }
};

const updateUsername = async () => {
  loading.value = true;
  try {
    const job = await auctionApi.users.update.mutate({
      username: username.value.trim(),
      referralCode: referralInfo.value?.referralCode || undefined,
    });

    await userStore.initUser();
    userStore.removeReferrerCode();
    handleClose();
  } catch (e) {
    usernameFormErrors.value = {
      type: "error",
      msg: t("pages.auth.form.errors.usernameExists"),
    };
  } finally {
    loading.value = false;
  }
};

const handleClose = () => {
  emits("on-close");
  storeApp.showOnboardingModal = true;
};

const removeReferral = () => {
  referralInfo.value = null;
};

const showReferralCodeModal = ref(false);
const handleAddReferral = (input: { username: string; referralCode: string }) => {
  referralInfo.value = {
    username: input.username,
    referralCode: input.referralCode,
  };
  showReferralCodeModal.value = false;
};

onMounted(async () => {
  try {
    loading.value = true;
    const refCode = userStore.getReferrerCode();
    const resp = await auctionApi.users.referrals.checkReferralCode.query({
      referralCode: refCode,
    });
    if (resp.exists) {
      referralInfo.value = {
        username: resp.username as string,
        referralCode: refCode,
      };
    }
  } catch (e) {
    // Ignore
  } finally {
    loading.value = false;
  }
});
</script>

<template>
  <Modal
    bgColor="bg-black"
    modalClasses=""
    :show="true"
    :showCloseButton="false"
    size="lg"
    title=""
  >
    <ModalAddReferral
      v-if="showReferralCodeModal"
      @on-referrer="handleAddReferral"
      @on-close="showReferralCodeModal = false"
    />

    <div class="flex flex-col items-center w-full gap-4">
      <div class="flex flex-col justify-center m-4 w-full gap-5 max-w-96">
        <div class="flex flex-col gap-1">
          <div class="font-medium text-center text-2xl mb-2">
            {{ t("pages.auth.form.username") }}
          </div>
          <Input
            :placeholder="t('pages.auth.form.signup.enter-username')"
            :center-text="true"
            :state="usernameFormErrors"
            class="mt-4"
            @change="handleUsernameOnChange"
            :disabled="loading"
          />
        </div>

        <div class="flex items-center justify-center">
          <div
            v-if="referralInfo"
            class="bg-zinc-900 text-sm p-2 rounded-lg w-full flex items-center border border-zinc-800 gap-2"
          >
            <div class="p-3"><Users /></div>
            <div class="w-full">
              <span class="text-neutral-300">{{
                t("pages.auth.form.signup.you-are-being-referred")
              }}</span>
              <span class="text-orange-40 ml-2">{{ referralInfo.username }}</span>
            </div>
            <div
              @click="removeReferral"
              class="p-2 bg-zinc-800 rounded-lg mr-1 text-neutral-400 cursor-pointer hover:bg-white group"
            >
              <X class="h-5 group-hover:text-black" />
            </div>
          </div>
          <Button
            v-else
            size="xs"
            :type="'secondary'"
            class="w-full"
            @click="showReferralCodeModal = true"
          >
            + {{ t("pages.auth.form.signup.add-referral-code") }}
          </Button>
        </div>

        <Button
          :disabled="usernameFormErrors?.type === 'error' || username.trim().length === 0"
          :loading="loading"
          @click="updateUsername"
          size="sm"
          type="primary-blue"
          class="w-full"
        >
          {{ t("_shared.submit") }}
        </Button>
      </div>
    </div>
  </Modal>
</template>

<style scoped lang="scss"></style>
